import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Award, BadgeCheck, Scale, HeartPulse, Linkedin, Instagram, Facebook } from 'lucide-react';

const Footer: React.FC = () => {
  return (
    <footer className="relative bg-silver-950 text-white overflow-hidden">
      {/* Subtle gradient orb */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-brand-600/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute inset-0 dot-grid opacity-[0.03]" />

      {/* Certifications */}
      <div className="relative border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
            {[
              { name: 'ISO 9001:2015', icon: <Award className="w-4 h-4 text-brand-400" /> },
              { name: 'FSSAI Certified', icon: <BadgeCheck className="w-4 h-4 text-brand-400" /> },
              { name: 'BIS Approved', icon: <Scale className="w-4 h-4 text-brand-400" /> },
              { name: 'WHO Standards', icon: <HeartPulse className="w-4 h-4 text-brand-400" /> },
            ].map((cert) => (
              <div key={cert.name} className="flex items-center gap-3 group">
                <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-brand-500/20 group-hover:border-brand-500/30 transition-all duration-300">
                  {cert.icon}
                </div>
                <span className="text-xs font-medium text-silver-400 group-hover:text-silver-300 transition-colors">{cert.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">

          {/* Brand Column */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src="/Aerohix-logo.jpeg"
                  alt="Arohix Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="leading-none">
                <span className="text-base font-extrabold text-white">Arohix</span>
                <span className="block text-[8px] font-semibold tracking-[0.2em] uppercase text-silver-500 mt-px">by Nathkrupa Energy</span>
              </div>
            </div>
            <p className="text-silver-500 text-sm leading-relaxed mb-6 max-w-xs">
              Where advanced 7-stage purification meets absolute transparency. Premium water, engineered for India.
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 mb-6">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[11px] text-silver-400 font-medium">A Product of Nathkrupa Energy</span>
            </div>
            <div className="flex items-center gap-3">
              <a href="https://www.linkedin.com/company/arohix-pure-water/" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#0077B5]/20 hover:border-[#0077B5]/40 transition-all duration-300 group">
                <Linkedin className="w-4 h-4 text-silver-400 group-hover:text-[#0077B5] transition-colors" />
              </a>
              <a href="https://www.instagram.com/arohix_pure_water/" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-pink-500/20 hover:border-pink-500/40 transition-all duration-300 group">
                <Instagram className="w-4 h-4 text-silver-400 group-hover:text-pink-400 transition-colors" />
              </a>
              <a href="https://www.facebook.com/profile.php?id=61587877106606" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#1877F2]/20 hover:border-[#1877F2]/40 transition-all duration-300 group">
                <Facebook className="w-4 h-4 text-silver-400 group-hover:text-[#1877F2] transition-colors" />
              </a>
            </div>
          </div>

          {/* Links */}
          <div className="lg:col-span-2">
            <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-silver-300 mb-5">Navigate</h4>
            <ul className="space-y-2.5">
              {[
                { name: 'Home', path: '/' },
                { name: 'About Us', path: '/about' },
                { name: 'Our Science', path: '/science' },
                { name: 'Products', path: '/products' },
                { name: 'Contact', path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-silver-500 hover:text-white text-sm transition-colors duration-200">{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div className="lg:col-span-3">
            <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-silver-300 mb-5">Products</h4>
            <ul className="space-y-2.5">
              {['Arohix — 200ml', 'Arohix — 500ml', 'Arohix — 1 Litre', 'Custom Branding Solutions'].map((item) => (
                <li key={item}>
                  <Link to="/products" className="text-silver-500 hover:text-white text-sm transition-colors duration-200">{item}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-silver-300 mb-5">Contact</h4>
            <ul className="space-y-3.5">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-brand-400 mt-0.5 flex-shrink-0" />
                <span className="text-silver-500 text-sm">Metro complex, Kuldharan road,<br />Karjat, Dist. Anagar,<br />Maharashtra 414402</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-brand-400 flex-shrink-0" />
                <a href="tel:+18002332727" className="text-silver-500 hover:text-white text-sm transition-colors">1800-233-2727</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-brand-400 flex-shrink-0" />
                <a href="mailto:nathkrupaenergy@gmail.com" className="text-silver-500 hover:text-white text-sm transition-colors">nathkrupaenergy@gmail.com</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="relative border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-silver-600 text-xs">
            &copy; {new Date().getFullYear()} Arohix Water — A Product of Nathkrupa Energy
          </p>
          <p className="text-silver-700 text-[10px] tracking-wider">
            B2B Water Supplier India &middot; Bulk Packaged Drinking Water &middot; Ozonated Mineral Water
          </p>
          <p className="text-silver-500 text-xs">
            Powered by{' '}
            <a
              href="https://balsoordigital.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-400 hover:text-white transition-colors duration-200 underline underline-offset-2"
            >
              Balsoor Digital
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
